import { useSelector } from "react-redux";
import { isValidNumber } from "src/components/core/IntlTelInput/IntlTelInput";
import i18n from "src/locales";
import { isNursing } from "src/utils/pace/planTimeAdjustmentNursing";
import {
  CollapsedEnrollmentData,
  requestEnrollment,
} from "src/utils/api/enrollment";
import { trackEvent } from "src/utils/api/tracker";
import { getLanguage } from "src/utils/meristemContext";
import { prepareDataForBackend } from "src/utils/pace";
import { shouldAskForPhoneNumber } from "src/utils/payment";
import { CoreReduxState } from "src/utils/redux/internal";
import { updateCheckoutState } from "src/utils/redux/slices/checkout";
import {
  EnrollmentErrors,
  PaymentEnrollmentFormState,
  updatePaymentEnrollmentFormState,
} from "src/utils/redux/slices/paymentEnrollmentForm";
import { errorConstants } from "src/utils/services/PurchaseErrorMessages";
import { genderConstantsMapping } from "src/utils/sexAndGender";
import { isHM, isUS } from "src/utils/userSegment";
import {
  isCollapsedPaymentEligible,
  hasExistingAcccount,
} from "src/utils/userSegment/features";
import { isEmail, isName } from "src/utils/validate";
import { useCallbackRef, useOnce } from "./lifecycle";
import {
  useAppDispatch,
  useCheckout,
  usePromoCode,
  useUserData,
} from "./redux";
import { useSurveyAnswers } from "./survey/answers";

export function useEnrollmentForm() {
  const checkout = useCheckout();
  const promoCode = usePromoCode();
  const enrollmentForm = useSelector(
    (state: CoreReduxState) => state.paymentEnrollmentForm
  );
  const { enrollmentInfo, enrollmentErrors = {} } = enrollmentForm;
  const hasEnrollmentErrors = Object.values(enrollmentErrors).some(Boolean);

  const dispatch = useAppDispatch();

  function updateEnrollmentInfo(
    newEnrollmentInfo: PaymentEnrollmentFormState["enrollmentInfo"]
  ) {
    dispatch(
      updatePaymentEnrollmentFormState({
        enrollmentInfo: newEnrollmentInfo,
        enrollmentErrors: validateEnrollmentInfo(newEnrollmentInfo),
      })
    );
  }

  // TODO: See if there is a better way to seed this
  // Set default values and default errors for enrollment form
  const userData = useUserData();
  useOnce(() => {
    if (!enrollmentInfo.name && !enrollmentInfo.email) {
      const newEnrollmentInfo = {
        ...enrollmentInfo,
        name: userData.name,
        email: userData.email,
      };
      updateEnrollmentInfo(newEnrollmentInfo);
    }
  });

  function setField(
    values: Omit<
      Partial<PaymentEnrollmentFormState["enrollmentInfo"]>,
      "termAgreement"
    >
  ) {
    const newEnrollmentInfo = {
      ...enrollmentInfo,
      ...values,
    };

    ["password", "confirmPassword", "email"].forEach((field) => {
      if (field in values) {
        newEnrollmentInfo[field] = values[field].replace(/\s/g, "");
      }
    });

    updateEnrollmentInfo(newEnrollmentInfo);
    dispatch(updateCheckoutState({ userStartedEnteringPaymentInfo: true }));
  }

  function hasVisibleError(field?: keyof EnrollmentErrors) {
    const { showPaymentErrors } = checkout;

    if (!showPaymentErrors) {
      return false;
    }

    return field ? !!enrollmentErrors[field] : hasEnrollmentErrors;
  }

  function setTermAgreement(termAgreement: boolean) {
    dispatch(
      updatePaymentEnrollmentFormState({
        enrollmentInfo: {
          ...enrollmentInfo,
          termAgreement,
        },
        enrollmentErrors: {
          ...enrollmentErrors,
          // Will be re-evaled on submit
          termAgreement: "",
        },
      })
    );
  }
  function validateTermAgreement() {
    if (!promoCode.promoCodeIsVip && isUS() && !enrollmentInfo.termAgreement) {
      const errorMessage = i18n.t("payment:terms:error");
      dispatch(
        updatePaymentEnrollmentFormState({
          enrollmentErrors: {
            ...enrollmentErrors,
            termAgreement: errorMessage,
          },
        })
      );
      return {
        termAgreement: errorMessage,
      };
    }
    return undefined;
  }

  function validateForm() {
    if (
      !promoCode.promoCodeIsVip &&
      isCollapsedPaymentEligible() &&
      hasEnrollmentErrors
    ) {
      return enrollmentErrors;
    }
    return undefined;
  }

  function applyError(error: errorConstants) {
    const errorMap = {
      [errorConstants.errorActiveSubscription]: {
        email: i18n.t(
          "enrollmentForm:inputFields:email:errorMessages:activeSubscription"
        ),
      },
      [errorConstants.errorInvalidEmail]: {
        email: i18n.t("enrollmentForm:inputFields:email:errorMessages:invalid"),
      },
      [errorConstants.errorTermAgreement]: {
        termAgreement: i18n.t("payment:terms:error"),
      },
    };

    const errorState = errorMap[error];
    if (errorState) {
      dispatch(
        updatePaymentEnrollmentFormState({
          enrollmentErrors: {
            ...enrollmentErrors,
            ...errorState,
          },
        })
      );
      return true;
    }

    return false;
  }

  return {
    enrollmentInfo,
    setField: useCallbackRef(setField),
    updateEnrollmentInfo,

    validateForm: useCallbackRef(validateForm),

    enrollmentErrors,
    hasEnrollmentErrors,
    hasVisibleError: useCallbackRef(hasVisibleError),
    applyError: useCallbackRef(applyError),

    setTermAgreement: useCallbackRef(setTermAgreement),
    validateTermAgreement: useCallbackRef(validateTermAgreement),
  };
}

export function useEnroll() {
  const { enrollmentInfo } = useEnrollmentForm();
  const surveyAnswers = useSurveyAnswers();

  async function enroll(curriculum?: string) {
    // Track survey answer fields that do not exist and populate with default values
    const missingFields = [];
    let { gender, heightCm, weightKg, ageRange } = surveyAnswers;
    let genderMapping: string;
    if (!gender) {
      missingFields.push("gender");
      genderMapping = genderConstantsMapping.UNKNOWN;
    } else {
      genderMapping = genderConstantsMapping[gender[0]];
    }
    if (!heightCm) {
      missingFields.push("height");
      heightCm = 170;
    }
    if (!weightKg) {
      missingFields.push("weight");
      weightKg = 65;
    }
    if (!ageRange) {
      missingFields.push("age");
      ageRange = ["40s"];
    }
    // Fire off event only if missingFields is empty
    if (missingFields.length && !isHM()) {
      trackEvent("ProfileFieldMissing", {
        missingFields,
      });
    }
    const { email, name, password, phoneNumber } = enrollmentInfo;

    // Pass in data needed for CS
    const userProfileData: CollapsedEnrollmentData = {
      email,
      name,
      password,
      phoneNumber,
      language: getLanguage(),
      gender: genderMapping,
      heightInCm: heightCm,
      weightInKg: weightKg,
      age: parseInt(ageRange[0], 10),
      isNursing: isNursing(surveyAnswers),
    };

    if (!isHM()) {
      const weightLossData = prepareDataForBackend(surveyAnswers);
      if (weightLossData) {
        (userProfileData as any).weightLossData = weightLossData;
      }
    }

    return requestEnrollment({
      userProfileData,
      // Filter HW curriculum to maintain parity with prior impl
      // TODO: Check if the API is fine with this being passed
      curriculum: curriculum === "HW" ? undefined : curriculum,
    });
  }

  return {
    enroll: useCallbackRef(enroll),
  };
}

function validateEnrollmentInfo(
  enrollmentInfo: PaymentEnrollmentFormState["enrollmentInfo"]
) {
  const {
    email,
    password,
    confirmPassword,
    name,
    phoneNumber,
    phoneNumberCountryCode,
  } = enrollmentInfo;
  const enrollmentErrors: EnrollmentErrors = {
    name: false,
    email: false,
    password: false,
    confirmPassword: false,
    phoneNumber: false,
  };

  if (!isName(name)) {
    enrollmentErrors.name = i18n.t<string>(
      "enrollmentForm:inputFields:name:errorMessages:required"
    );
  }
  if (!isEmail(email)) {
    enrollmentErrors.email = i18n.t<string>(
      "enrollmentForm:inputFields:email:errorMessages:invalid"
    );
  }
  // Note(kevinh): bypass password validation on front end since the user's account is already assumed
  // to exist. CS will not validate password on their side in this case.
  if (!hasExistingAcccount()) {
    if (password?.length < 1) {
      enrollmentErrors.password = i18n.t<string>(
        "enrollmentForm:inputFields:password:errorMessages:required"
      );
    } else if (password?.length < 4) {
      enrollmentErrors.password = i18n.t<string>(
        "enrollmentForm:inputFields:password:errorMessages:minLength"
      );
    }
    if (password !== confirmPassword) {
      enrollmentErrors.confirmPassword = i18n.t<string>(
        "enrollmentForm:inputFields:confirmPassword:errorMessages:passwordsNotMatching"
      );
    }
  }
  if (
    shouldAskForPhoneNumber() &&
    !isValidNumber(phoneNumber, phoneNumberCountryCode)
  ) {
    enrollmentErrors.phoneNumber = i18n.t<string>(
      "enrollmentForm:inputFields:phoneNumber:errorMessages:invalid"
    );
  }
  return enrollmentErrors;
}
